import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import PercentageChangeText from "@/lib/molecules/percentageChangeText";
import { getPeriodType } from "@/utils/periods";

interface ComparisonTableProps {
    columnNames: string[];
    period: string;
    showPreviousYear: boolean;
    periodData: { value: number }[];
    yearData: { value: number }[];
    currentData: { value: number }[];
    totalCurrent: number;
    totalPreviousPeriod: number;
    totalPreviousYear: number;
    valueFormatter: (value: number) => string;
    className?: string;
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({
    columnNames,
    period,
    showPreviousYear,
    periodData,
    yearData,
    currentData,
    totalCurrent,
    totalPreviousPeriod,
    totalPreviousYear,
    valueFormatter,
    className,
}) => {
    const percentageChangePrevious = totalPreviousPeriod === 0 ? 0 : ((totalCurrent - totalPreviousPeriod) / totalPreviousPeriod) * 100;

    const percentageChangePreviousYear = totalPreviousYear === 0 ? 0 : ((totalCurrent - totalPreviousYear) / totalPreviousYear) * 100;

    return (
        <div className={`w-full ${className}`}>
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        <TableHead className="w-[50px] h-7 font-normal p-0">
                            <div>Vs</div>
                            <div>Previous</div>
                        </TableHead>
                        {columnNames.map((name, index) => (
                            <TableHead key={index} className="text-right h-7  font-normal px-1 py-0">
                                {name}
                            </TableHead>
                        ))}
                        <TableHead className="text-right h-7  font-medium pl-1 pr-0 py-0">Overall</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {/* Period Row */}
                    <TableRow className="hover:bg-transparent">
                        <TableCell className="font-medium py-0.5 px-0 ">{getPeriodType(period)}</TableCell>
                        {periodData.map((item, index) => {
                            const percentageChange = item.value === 0 ? 0 : ((currentData[index].value - item.value) / item.value) * 100;
                            return (
                                <TableCell key={index} className="text-right py-0.5 px-1 ">
                                    <div className="whitespace-nowrap">{valueFormatter(item.value)}</div>
                                    <PercentageChangeText
                                        value={percentageChange}
                                        description=""
                                        valueFormatter={(value) => value.toFixed(0)}
                                    />
                                </TableCell>
                            );
                        })}
                        <TableCell className="text-right py-0.5 pl-1 pr-0 ">
                            {/* Addressable so a test can pin THIS window's overall value: the row
                                also carries a per-group cell that often renders the same string. */}
                            <div className="whitespace-nowrap" data-testid="comparison-overall-previous-period">
                                {valueFormatter(totalPreviousPeriod)}
                            </div>
                            <PercentageChangeText
                                value={percentageChangePrevious}
                                description=""
                                valueFormatter={(value) => value.toFixed(0)}
                                extraClasses="font-medium"
                            />
                        </TableCell>
                    </TableRow>

                    {/* Year Row */}
                    {showPreviousYear && (
                        <TableRow className="hover:bg-transparent">
                            <TableCell className="font-medium py-0.5 px-0 ">Year</TableCell>
                            {yearData.map((item, index) => {
                                const percentageChange =
                                    item.value === 0 ? 0 : ((currentData[index].value - item.value) / item.value) * 100;
                                return (
                                    <TableCell key={index} className="text-right py-0.5 px-1 ">
                                        <div className="whitespace-nowrap">{valueFormatter(item.value)}</div>
                                        <PercentageChangeText
                                            value={percentageChange}
                                            description=""
                                            valueFormatter={(value) => value.toFixed(0)}
                                        />
                                    </TableCell>
                                );
                            })}
                            <TableCell className="text-right py-0.5 pl-1 pr-0 ">
                                <div className="whitespace-nowrap" data-testid="comparison-overall-previous-year">
                                    {valueFormatter(totalPreviousYear)}
                                </div>
                                <PercentageChangeText
                                    value={percentageChangePreviousYear}
                                    description=""
                                    valueFormatter={(value) => value.toFixed(0)}
                                    extraClasses="font-medium"
                                />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

export default ComparisonTable;
